import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from "@/lib/i18n";
import {
  Eye, EyeOff, Lock, Unlock, Square, Circle, PenTool, Folder,
  ChevronRight, ChevronDown, Trash2
} from 'lucide-react';
import { useItemBounds, calculateMMBounds } from '@/components/svg-editor/use-item-bounds';
import paper from 'paper';
import ColorPalettePanel from './color-panel';
import StrokePanel from './stroke-panel';
import QuickTransformPanel from './size-panel';
import SettingsPanel from './settings-panel';
import {
  TooltipProvider,
} from '@/components/ui/tooltip';

export default function LayersPanel({
  items = [],
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onReorder,
  onDeleteItems,
  onBulkVisibility,
  onProjectChange,
    theme = 'dark',
    onThemeChange,
    glassType,
    setGlassType,
    showGrid,
    setShowGrid,
    showCrosshair,
    setShowCrosshair,
    snapToGuides,
    setSnapToGuides,
    onCenterView,
    viewMode,
    setViewMode,
    rawPaperItemsMap,
}) {
  const { t } = useTranslation('editor');
  const [openGroups, setOpenGroups] = useState({});
  const [draggedId, setDraggedId] = useState(null);
  const [visibilityMap, setVisibilityMap] = useState({});
  const [transformTick, setTransformTick] = useState(0);


  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentStrokeColor, setCurrentStrokeColor] = useState('#000000');
  const [currentStrokeWidth, setCurrentStrokeWidth] = useState(0);

  useEffect(() => {
    const handleTransform = () => {
      setTransformTick((prev) => (prev + 1) % 1000);
    };
    window.addEventListener('paper-item-transform', handleTransform);
    return () => {
      window.removeEventListener('paper-item-transform', handleTransform);
    };
  }, []);

  const forceRender = () => setTransformTick((prev) => prev + 1);

  const flatItems = useMemo(() => {
    const res = [];
    const walk = (list) => {
      (list || []).forEach((it) => {
        res.push(it);
        if (it.children && it.children.length) walk(it.children);
      });
    };
    walk(items || []);
    return res;
  }, [items]);

  const getDescendantIds = (item) => {
    const ids = [];
    const walk = (node) => {
      if (!node || !node.children) return;
      node.children.forEach((c) => {
        ids.push(String(c.id));
        walk(c);
      });
    };
    walk(item);
    return ids;
  };

  const activeSelectedIds = useMemo(() => {
    const set = new Set();
    flatItems.forEach((it) => {
      if (it.selected) {
        set.add(String(it.id));
        if (it.children && it.children.length) {
          getDescendantIds(it).forEach((d) => set.add(d));
        }
      }
    });
    return Array.from(set);
  }, [flatItems]);

  const getPaperItem = React.useCallback((id) => {
    if (rawPaperItemsMap) {
      if (rawPaperItemsMap[id]) return rawPaperItemsMap[id];
      if (rawPaperItemsMap[Number(id)]) return rawPaperItemsMap[Number(id)];
    }

    const activeProject =
      (paper && paper.project) ||
      (typeof window !== 'undefined' && window.paper && window.paper.project);

    if (!activeProject) return null;

    const numericId = Number(id);
    if (!Number.isNaN(numericId)) {
      const foundById = activeProject.getItem({ id: numericId });
      if (foundById) return foundById;
    }

    const foundByPredicate = activeProject.getItem((item) => {
      return (
        String(item.id) === String(id) ||
        (item.data && String(item.data.id) === String(id)) ||
        item.name === String(id)
      );
    });

    return foundByPredicate || null;
  }, [rawPaperItemsMap]);

  const isSingleSelected = activeSelectedIds.length === 1;
  const singleSelectedId = isSingleSelected ? activeSelectedIds[0] : null;

  useEffect(() => {
    if (isSingleSelected && singleSelectedId) {
      const pItem = getPaperItem(singleSelectedId);
      if (pItem) {
        const c = pItem.fillColor || pItem.strokeColor;
        if (c && c.toCSS) {
          try {
            setCurrentColor(c.toCSS(true));
          } catch (e) {}
        }

        if (pItem.strokeColor && pItem.strokeColor.toCSS) {
          try {
            setCurrentStrokeColor(pItem.strokeColor.toCSS(true));
          } catch (e) {}
        }
        setCurrentStrokeWidth(pItem.strokeWidth || 0);
      }
    }
  }, [singleSelectedId, isSingleSelected, transformTick, getPaperItem]);

  const handleApplyColor = (hexColor) => {
    if (!isSingleSelected || !singleSelectedId) return;

    setCurrentColor(hexColor);

    const applyToPaperNode = (item) => {
      if (!item) return;
      if (item.children && item.children.length) {
        item.children.forEach(applyToPaperNode);
      } else {
        if (item.fillColor || (!item.strokeColor && item.strokeWidth)) {
          item.fillColor = hexColor;
          if (!item.data) item.data = {};
          if (!item.data.origStyle) {
            const type = item.data?.type;
            const isText = item instanceof paper.PointText;
            const isLineOrArrow = type === 'line' || type === 'arrow';
            const isArrowHead = item.closed && type === 'arrow';

            item.data.origStyle = {
              fillColor: hexColor,
              strokeColor: item.strokeColor ? item.strokeColor.toCSS(true) : (isText ? '#ef4444' : '#000000'),
              strokeWidth: item.strokeWidth || (isText ? 1.5 : 1),
              strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : (isText ? true : false)
            };
          } else {
            item.data.origStyle.fillColor = hexColor;
          }
        } else {
          item.strokeColor = hexColor;
          if (!item.data) item.data = {};
          if (!item.data.origStyle) {
            const type = item.data?.type;
            const isText = item instanceof paper.PointText;
            const isLineOrArrow = type === 'line' || type === 'arrow';
            const isArrowHead = item.closed && type === 'arrow';

            item.data.origStyle = {
              fillColor: isText ? (item.fillColor ? item.fillColor.toCSS(true) : '#ef4444') : (isLineOrArrow ? (isArrowHead ? '#000000' : null) : (item.fillColor ? (item.fillColor.alpha === 0 ? 'transparent' : item.fillColor.toCSS(true)) : '#d59029')),
              strokeColor: hexColor,
              strokeWidth: item.strokeWidth || (isText ? 1.5 : 1),
              strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : (isText ? true : false)
            };
          } else {
            item.data.origStyle.strokeColor = hexColor;
          }
        }
      }
    };

    const pItem = getPaperItem(singleSelectedId);
    if (pItem) {
      applyToPaperNode(pItem);

      const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
      if (activeProject?.view) {
        activeProject.view.update();
      }

      window.dispatchEvent(new CustomEvent('paper-item-transform'));
      if (typeof onProjectChange === 'function') onProjectChange();
    }
  };

  const handleApplyStrokeColor = (hexColor) => {
    if (!isSingleSelected || !singleSelectedId) return;

    setCurrentStrokeColor(hexColor);

    const applyToPaperNode = (item) => {
      if (!item) return;
      if (item.children && item.children.length) {
        item.children.forEach(applyToPaperNode);
      } else {
        item.strokeColor = hexColor;
        if (!item.data) item.data = {};
        if (!item.data.origStyle) {
          const type = item.data?.type;
          const isText = item instanceof paper.PointText;
          const isLineOrArrow = type === 'line' || type === 'arrow';
          const isArrowHead = item.closed && type === 'arrow';

          item.data.origStyle = {
            fillColor: isText ? (item.fillColor ? item.fillColor.toCSS(true) : '#ef4444') : (isLineOrArrow ? (isArrowHead ? '#000000' : null) : (item.fillColor ? (item.fillColor.alpha === 0 ? 'transparent' : item.fillColor.toCSS(true)) : '#d59029')),
            strokeColor: hexColor,
            strokeWidth: item.strokeWidth || (isText ? 1.5 : 1),
            strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : (isText ? true : false)
          };
        } else {
          item.data.origStyle.strokeColor = hexColor;
        }

        if (item.data?.strokeOutlines) {
          item.data.strokeOutlines.forEach(id => {
            const p = paper.project.getItem({ id: Number(id) });
            if (p) {
              p.strokeColor = hexColor;
              if (p.data?.origStyle) {
                p.data.origStyle.strokeColor = hexColor;
              }
            }
          });
        }
      }
    };

    const pItem = getPaperItem(singleSelectedId);
    if (pItem) {
      applyToPaperNode(pItem);

      const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
      if (activeProject?.view) {
        activeProject.view.update();
      }

      window.dispatchEvent(new CustomEvent('paper-item-transform'));
      if (typeof onProjectChange === 'function') onProjectChange();
    }
  };

  const handleApplyStrokeWidth = (width) => {
    if (!isSingleSelected || !singleSelectedId) return;

    setCurrentStrokeWidth(width);

    const applyToPaperNode = (item) => {
      if (!item) return;
      if (item.children && item.children.length) {
        item.children.forEach(applyToPaperNode);
      } else {
        item.strokeWidth = width;
        if (!item.data) item.data = {};
        if (!item.data.origStyle) {
          const type = item.data?.type;
          const isText = item instanceof paper.PointText;
          const isLineOrArrow = type === 'line' || type === 'arrow';
          const isArrowHead = item.closed && type === 'arrow';

          item.data.origStyle = {
            fillColor: isText ? (item.fillColor ? item.fillColor.toCSS(true) : '#ef4444') : (isLineOrArrow ? (isArrowHead ? '#000000' : null) : (item.fillColor ? (item.fillColor.alpha === 0 ? 'transparent' : item.fillColor.toCSS(true)) : '#d59029')),
            strokeColor: item.strokeColor ? item.strokeColor.toCSS(true) : (isText ? '#ef4444' : '#000000'),
            strokeWidth: width,
            strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : (isText ? true : false)
          };
        } else {
          item.data.origStyle.strokeWidth = width;
        }
      }
    };

    const pItem = getPaperItem(singleSelectedId);
    if (pItem) {
      applyToPaperNode(pItem);

      const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
      if (activeProject?.view) {
        activeProject.view.update();
      }

      window.dispatchEvent(new CustomEvent('paper-item-transform'));
      if (typeof onProjectChange === 'function') onProjectChange();
    }
  };

  const handleGenerateOutlines = () => {
    if (!isSingleSelected || !singleSelectedId) return;

    const pItem = getPaperItem(singleSelectedId);
    if (!pItem) return;

    const generateForNode = (item) => {
      if (!item) return;
      if (item.children && item.children.length) {
        item.children.forEach(generateForNode);
      } else {
        const width = item.strokeWidth;
        try {
          if (item.data?.strokeOutlines) {
            item.data.strokeOutlines.forEach(id => {
              const p = paper.project.getItem({ id: Number(id) });
              if (p) p.remove();
            });
            item.data.strokeOutlines = [];
          }

          if (width > 0 && item.strokeColor) {
            const createOutline = (offset, name) => {
              const outline = item.clone();
              outline.fillColor = null;
              outline.strokeColor = item.strokeColor;
              outline.strokeWidth = 1 / (paper.view.zoom || 1);
              outline.name = `${item.name || 'Об\'єкт'} (${name})`;
              outline.data = { ...outline.data, isSystem: false, parentId: item.id, strokeOutlines: [], isOutline: true };
              const parentStrokeColor = item.strokeColor ? (item.strokeColor.toCSS ? item.strokeColor.toCSS(true) : item.strokeColor) : '#000000';
              
              if (outline.data.origStyle) {
                outline.data.origStyle = { 
                  ...outline.data.origStyle, 
                  fillColor: null,
                  strokeColor: parentStrokeColor,
                  strokeWidth: 1,
                  strokeScaling: false 
                };
              } else {
                outline.data.origStyle = {
                  fillColor: null,
                  strokeColor: parentStrokeColor,
                  strokeWidth: 1,
                  strokeScaling: false
                };
              }
              
              if (item.closed && item.bounds.width > 0 && item.bounds.height > 0) {
                const bounds = item.geometricBounds || item.bounds;

                const type = item.data?.type;
                if ((item instanceof paper.Path) && 
                    (type === 'rect' || type === 'rectangle' || type === 'square' || (item.segments && item.segments.length === 4 && !type))) {
                  const newBounds = bounds.expand(offset * 2);
                  outline.bounds = newBounds;
                } else if (type === 'circle' || type === 'oval' || type === 'ellipse' || ((item instanceof paper.Path) && item.segments && item.segments.length > 8)) {

                  const scaleX = (bounds.width + offset * 2) / bounds.width;
                  const scaleY = (bounds.height + offset * 2) / bounds.height;
                  outline.scale(scaleX, scaleY, bounds.center);
                } else if (type === 'triangle' || type === 'rounded-triangle') {

                  const segments = item.segments || [];
                  if (segments.length >= 3) {
                    const points = segments.map(s => s.point);

                    const centroid = bounds.center.add(new paper.Point(0, bounds.height / 6));

                    
                    const size = bounds.width;
                    const h = size * Math.sqrt(3) / 2;
                    const r = h / 3;
                    
                    const factor = Math.max(0.01, (r + offset) / r);
                    
                    outline.scale(factor, centroid);
                  } else {
                    const scaleX = (bounds.width + offset * 2) / bounds.width;
                    const scaleY = (bounds.height + offset * 2) / bounds.height;
                    outline.scale(scaleX, scaleY, bounds.center);
                  }
                } else if (type === 'hexagon' || type === 'rhombus' || type === 'polygon-n') {

                  const size = Math.min(bounds.width, bounds.height);
                  const r = size / 2;
                  const factor = (r + offset) / r;
                  outline.scale(factor, bounds.center);
                } else if (type === 'star') {

                  const size = Math.min(bounds.width, bounds.height);
                  const r = size / 2;
                  const factor = (r + offset) / r;
                  outline.scale(factor, bounds.center);
                } else {
                  const scaleX = (bounds.width + offset * 2) / bounds.width;
                  const scaleY = (bounds.height + offset * 2) / bounds.height;
                  outline.scale(scaleX, scaleY, bounds.center);
                }
              }
              
              return outline;
            };

            const outer = createOutline(width / 2, t('colors.outer_line', { defaultValue: 'зовнішня лінія' }));
            const inner = createOutline(-width / 2, t('colors.inner_line', { defaultValue: 'внутрішня лінія' }));
            

            if (item.parent) {
              item.parent.addChild(outer);
              item.parent.addChild(inner);
            }
            
            item.data.strokeOutlines = [outer.id, inner.id];
          }
        } catch (e) {
          console.error("Error creating stroke outlines", e);
        }
      }
    };

    generateForNode(pItem);
    
    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) {
      activeProject.view.update();
    }
    
    if (typeof onProjectChange === 'function') onProjectChange();
  };

  useEffect(() => {
    const newMap = {};
    flatItems.forEach((it) => {
      const pItem = getPaperItem(it.id);
      const cleanId = String(it.id);
      if (pItem) {
        newMap[cleanId] = pItem.visible;
      } else {
        newMap[cleanId] = it.visible !== false;
      }
    });
    setVisibilityMap(newMap);
  }, [flatItems, getPaperItem]);

  const selectedPaperObjects = useMemo(() => {
    return activeSelectedIds.map(getPaperItem).filter(Boolean);
  }, [activeSelectedIds, getPaperItem]);

  useItemBounds(selectedPaperObjects);

  const itemBoundsMap = useMemo(() => {
    const activeProject =
      (paper && paper.project) ||
      (typeof window !== 'undefined' && window.paper && window.paper.project);

    if (!activeProject) return {};

    const map = {};
    flatItems.forEach((it) => {
      const pItem = getPaperItem(it.id);
      if (pItem) {
        const b = calculateMMBounds(pItem);
        if (b) map[String(it.id)] = b;
      }
    });

    return map;
  }, [flatItems, getPaperItem]);

  const allSelected = flatItems.length > 0 && activeSelectedIds.length === flatItems.length;

  const toggleGroup = (id, e) => {
    e.stopPropagation();
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCheck = (id, e) => {
    if (e) e.stopPropagation();
    const idStr = String(id);
    const isCurrentlyChecked = activeSelectedIds.includes(idStr);
    const targetState = !isCurrentlyChecked;

    const targetItem = flatItems.find((it) => String(it.id) === idStr);
    if (!targetItem) return;

    const idsToToggle = new Set([idStr, ...getDescendantIds(targetItem)]);

    idsToToggle.forEach((itemId) => {
      const pItem = getPaperItem(itemId);
      if (pItem) pItem.selected = targetState;
    });

    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) activeProject.view.update();

    let newSelectedIds = [...activeSelectedIds];
    if (targetState) {
      idsToToggle.forEach((idVal) => {
        if (!newSelectedIds.includes(idVal)) newSelectedIds.push(idVal);

        const pItem = getPaperItem(idVal);
        if (pItem?.data?.strokeOutlines) {
          pItem.data.strokeOutlines.forEach(outId => {
            const outItem = paper.project.getItem({id: Number(outId)});
            if (outItem) {
              outItem.selected = true;
              const outIdStr = String(outId);
              if (!newSelectedIds.includes(outIdStr)) newSelectedIds.push(outIdStr);
            }
          });
        }
      });
    } else {
      idsToToggle.forEach((idVal) => {
         const pItem = getPaperItem(idVal);
         if (pItem?.data?.strokeOutlines) {
           pItem.data.strokeOutlines.forEach(outId => idsToToggle.add(String(outId)));
         }
      });
      newSelectedIds = newSelectedIds.filter((idVal) => !idsToToggle.has(idVal));
    }

    if (typeof onSelect === 'function') onSelect(newSelectedIds);
    if (typeof onProjectChange === 'function') onProjectChange();
  };

  const handleSelectAll = () => {
    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (allSelected) {
      if (activeProject) {
        try {
          activeProject.deselectAll && activeProject.deselectAll();
        } catch (e) {}
      }
      if (typeof onSelect === 'function') onSelect([]);
    } else {
      const allIds = flatItems.map((it) => String(it.id));
      flatItems.forEach((it) => {
        const pItem = getPaperItem(it.id);
        if (pItem) pItem.selected = true;
      });
      if (activeProject?.view) activeProject.view.update();
      if (typeof onSelect === 'function') onSelect(allIds);
    }
    if (typeof onProjectChange === 'function') onProjectChange();
  };

  const areAllSelectedVisible = useMemo(() => {
    if (!activeSelectedIds.length) return true;
    return activeSelectedIds.every((id) => {
      const cleanId = String(id);
      if (visibilityMap[cleanId] !== undefined) {
        return visibilityMap[cleanId];
      }
      const pItem = getPaperItem(cleanId);
      if (pItem) return pItem.visible;
      const flatIt = flatItems.find((it) => String(it.id) === cleanId);
      return flatIt ? flatIt.visible !== false : true;
    });
  }, [activeSelectedIds, flatItems, visibilityMap, getPaperItem]);

  const handleToggleBulkVisibility = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!activeSelectedIds.length) return;

    const nextState = !areAllSelectedVisible;

    setVisibilityMap((prev) => {
      const next = { ...prev };
      activeSelectedIds.forEach((id) => {
        next[String(id)] = nextState;
      });
      return next;
    });

    activeSelectedIds.forEach((id) => {
      const pItem = getPaperItem(id);
      if (pItem) pItem.visible = nextState;
    });

    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) activeProject.view.update();
    window.dispatchEvent(new CustomEvent('paper-item-visibility-changed'));

    if (typeof onBulkVisibility === 'function') {
      onBulkVisibility(activeSelectedIds, nextState);
    } else if (typeof onToggleVisibility === 'function') {
      activeSelectedIds.forEach((id) => onToggleVisibility(id, nextState));
    }

    if (typeof onProjectChange === 'function') onProjectChange();
    forceRender();
  };

  const handleItemVisibility = (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const cleanId = String(id);
    const pItem = getPaperItem(cleanId);

    const currentIsVisible =
      visibilityMap[cleanId] !== undefined
        ? visibilityMap[cleanId]
        : pItem
          ? pItem.visible
          : true;

    const nextVisibility = !currentIsVisible;

    setVisibilityMap((prev) => ({
      ...prev,
      [cleanId]: nextVisibility,
    }));

    if (pItem) {
      pItem.visible = nextVisibility;
      if (!nextVisibility) {
        pItem.selected = false;
      }
    }

    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) {
      activeProject.view.update();
    }
    window.dispatchEvent(new CustomEvent('paper-item-visibility-changed'));

    if (typeof onToggleVisibility === 'function') {
      onToggleVisibility(cleanId, nextVisibility);
    }
    if (typeof onProjectChange === 'function') {
      onProjectChange();
    }

    forceRender();
  };

  const handleBulkDelete = () => {
    if (typeof onDeleteItems === 'function') {
      onDeleteItems(activeSelectedIds);
    } else {
      activeSelectedIds.forEach((id) => {
        const pItem = getPaperItem(id);
        if (pItem) pItem.remove();
      });
      if (typeof onReorder === 'function') onReorder();
    }
    const activeProject = paper?.project || (typeof window !== 'undefined' && window.paper?.project);
    if (activeProject?.view) activeProject.view.update();
    if (typeof onProjectChange === 'function') onProjectChange();
    window.dispatchEvent(new CustomEvent('paper-item-deleted'));
  };

  const handleDragStart = (e, id) => {
    e.stopPropagation();
    setDraggedId(String(id));
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedId || String(draggedId) === String(targetId)) return;

    if (typeof onReorder === 'function') {
      const parse = (s) => {
        const n = Number(s);
        return String(n) === String(s) ? n : s;
      };
      onReorder(parse(draggedId), parse(targetId));
    }
    setDraggedId(null);
  };

  const getItemIcon = (className) => {
    switch (className) {
      case 'Group':
        return <Folder className="w-3.5 h-3.5 text-indigo-400" />;
      case 'Path':
        return <PenTool className="w-3.5 h-3.5 text-amber-400" />;
      case 'CompoundPath':
        return <PenTool className="w-3.5 h-3.5 text-purple-400" />;
      case 'Shape':
        return <Square className="w-3.5 h-3.5 text-blue-400" />;
      default:
        return <Circle className="w-3.5 h-3.5 text-emerald-400" />;
    }
  };

  const renderTreeItem = (item, level = 0) => {
    const cleanId = String(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = !!openGroups[item.id];
    const isChecked = activeSelectedIds.includes(cleanId);

    const pItem = getPaperItem(cleanId);
    const isVisible =
      visibilityMap[cleanId] !== undefined
        ? visibilityMap[cleanId]
        : pItem
          ? pItem.visible
          : item.visible !== false;

    let itemColor = null;
    if (pItem) {
      if (pItem.fillColor && pItem.fillColor.alpha > 0) {
        itemColor = pItem.fillColor.toCSS(true);
      } else if (pItem.strokeColor && pItem.strokeWidth > 0) {
        itemColor = pItem.strokeColor.toCSS(true);
      } else if (pItem.data?.origStyle) {
        const style = pItem.data.origStyle;
        if (style.fillColor && style.fillColor !== 'none') {
          itemColor = style.fillColor;
        } else if (style.strokeColor && style.strokeColor !== 'none') {
          itemColor = style.strokeColor;
        }
      }
    }

    return (
      <div key={item.id} className="flex flex-col gap-0.5">
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, item.id)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, item.id)}
          onClick={(e) => handleCheck(item.id, e)}
          style={{ paddingLeft: `${level * 12 + 8}px` }}
          className={`flex items-center justify-between p-1.5 rounded text-xs transition-colors cursor-pointer border ${
            draggedId === cleanId ? 'opacity-40 border-dashed border-indigo-400' : ''
          } ${
            isChecked
              ? theme === 'dark'
                ? 'bg-zinc-800 border-indigo-500 text-white'
                : 'bg-indigo-50 border-indigo-300 text-indigo-900 font-medium'
              : theme === 'dark'
                ? 'bg-zinc-950/50 border-transparent hover:bg-zinc-800/50 text-zinc-300'
                : 'bg-white border-slate-100 hover:bg-slate-50 text-slate-700'
          }`}
        >
          <div className="flex items-center gap-1.5 overflow-hidden">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => handleCheck(item.id, e)}
              onClick={(e) => e.stopPropagation()}
              className="w-3 h-3 rounded accent-indigo-500 cursor-pointer"
            />

            {hasChildren && (
              <button
                type="button"
                onClick={(e) => toggleGroup(item.id, e)}
                className={`p-0.5 hover:text-white transition-colors ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
            )}

            {itemColor && (
              <div
                className="w-2.5 h-2.5 rounded-full border border-white/20 shadow-sm shrink-0"
                style={{ backgroundColor: itemColor }}
              />
            )}

            {getItemIcon(item.className)}
            <span className={`truncate max-w-[120px] ${!isVisible ? (theme === 'dark' ? 'line-through text-zinc-500 opacity-60' : 'line-through text-slate-400 opacity-60') : ''}`}>
              {item.name || `${t(`layers.${item.className.toLowerCase()}`, { defaultValue: item.className })} #${item.id}`}
            </span>
          </div>

          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            {itemBoundsMap[cleanId] && (
              <span className={`ml-2 text-[10px] font-mono ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}>
                {`${itemBoundsMap[cleanId].maxDiameterMm.toFixed(2)} мм`}
              </span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onToggleLock) onToggleLock(item.id);
              }}
              className={`p-1 rounded transition-colors ${
                item.locked ? 'text-amber-400' : theme === 'dark' ? 'text-zinc-500 hover:bg-zinc-700' : 'text-slate-400 hover:bg-slate-200'
              }`}
            >
              {item.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </button>

            <button
              type="button"
              onClick={(e) => handleItemVisibility(item.id, e)}
              className={`p-1 rounded transition-colors ${
                !isVisible ? 'text-zinc-600 hover:text-zinc-400' : (theme === 'dark' ? 'text-zinc-200 hover:text-white' : 'text-slate-700 hover:text-slate-900')
              }`}
            >
              {isVisible ? (
                <Eye className="w-3.5 h-3.5 text-emerald-400" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 text-neutral-500" />
              )}
            </button>
          </div>
        </div>

        {hasChildren && isOpen && (
          <div className="flex flex-col gap-0.5">
            {item.children.map((child) => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`w-80 h-full max-h-[85vh] border rounded-lg p-3 flex flex-col gap-2 select-none transition-colors duration-300 ${
      theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <TooltipProvider delayDuration={150}>
        <SettingsPanel
          theme={theme}
          onThemeChange={onThemeChange}
          glassType={glassType}
          setGlassType={setGlassType}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          showCrosshair={showCrosshair}
          setShowCrosshair={setShowCrosshair}
          snapToGuides={snapToGuides}
          setSnapToGuides={setSnapToGuides}
          onCenterView={onCenterView}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        <QuickTransformPanel
        selectedPaperObjects={selectedPaperObjects}
        onProjectChange={onProjectChange}
        theme={theme}
      />

      <ColorPalettePanel
        currentColor={currentColor}
        isSingleSelected={isSingleSelected}
        activeSelectedCount={activeSelectedIds.length}
        onApplyColor={handleApplyColor}
        theme={theme}
      />

      <StrokePanel
        strokeColor={currentStrokeColor}
        strokeWidth={currentStrokeWidth}
        isSingleSelected={isSingleSelected}
        onApplyStrokeColor={handleApplyStrokeColor}
        onApplyStrokeWidth={handleApplyStrokeWidth}
        onGenerateOutlines={handleGenerateOutlines}
        theme={theme}
      />

      <div className={`flex items-center justify-between border-b pb-2 pt-1 ${
        theme === 'dark' ? 'border-zinc-800' : 'border-slate-100'
      }`}>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
            className="w-3.5 h-3.5 rounded accent-indigo-500 cursor-pointer"
          />
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}>{t('layers.title')}</h3>
        </div>
        <span className={`text-xs ${theme === 'dark' ? 'text-zinc-500' : 'text-slate-400'}`}>{flatItems.length} {t('layers.items_count')}</span>
      </div>

      <div className={`flex items-center justify-between border p-1.5 rounded text-xs transition-opacity ${
        activeSelectedIds.length > 0 
          ? (theme === 'dark' ? 'bg-indigo-950/40 border-indigo-900/50' : 'bg-indigo-50 border-indigo-100')
          : (theme === 'dark' ? 'bg-zinc-800/30 border-zinc-700/50 opacity-50' : 'bg-slate-50 border-slate-100 opacity-50')
      }`}>
        <span className={`font-medium ${
          activeSelectedIds.length > 0
            ? (theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600')
            : (theme === 'dark' ? 'text-zinc-500' : 'text-slate-400')
        }`}>
          {t('header.selected')}: {activeSelectedIds.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={activeSelectedIds.length === 0}
            title={areAllSelectedVisible ? t('layers.visibility_hide') : t('layers.visibility_show')}
            onClick={handleToggleBulkVisibility}
            className={`p-1 rounded transition-colors ${
              activeSelectedIds.length === 0
                ? 'cursor-not-allowed text-zinc-600'
                : !areAllSelectedVisible 
                  ? (theme === 'dark' ? 'text-zinc-300 hover:bg-zinc-800' : 'text-slate-500 hover:bg-white') 
                  : 'text-emerald-400 hover:bg-zinc-800'
            }`}
          >
            {!areAllSelectedVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            disabled={activeSelectedIds.length === 0}
            title={t('layers.delete_selected')}
            onClick={handleBulkDelete}
            className={`p-1 rounded transition-colors ${
              activeSelectedIds.length === 0
                ? 'cursor-not-allowed text-zinc-600'
                : 'text-red-400 ' + (theme === 'dark' ? 'hover:bg-zinc-800' : 'hover:bg-white')
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0 gap-1 overflow-y-auto pr-1 scrollbar-thin">
        {items.length === 0 ? (
          <div className={`text-xs text-center py-8 ${theme === 'dark' ? 'text-zinc-500' : 'text-slate-400'}`}>{t('layers.no_items')}</div>
        ) : (
          items.map((item) => renderTreeItem(item, 0))
        )}
      </div>
      </TooltipProvider>
    </div>
  );
}